import React, { Dispatch, FC, ReactElement, ReactNode, useState as reactUseState, SetStateAction, useMemo, useState } from 'react';
import { Layout, Breadcrumb, Row, Col, Avatar, theme, Dropdown, Alert, Badge, Popover } from 'antd';
import {  ExclamationCircleOutlined, LeftOutlined, MenuOutlined, RightOutlined } from '@ant-design/icons';
import { hookstate as createState, State, useHookstate as hookstateUseState } from '@hookstate/core';
import { Link } from 'react-router-dom';
import i18next from 'i18next';
import moment from 'moment';
import HttpService from '../services/http.service';
import { AuthenticationService } from '../services/authentication';
import { Select } from 'antd';
import { MenuProps } from 'antd/lib';
import useScreenSize from './hooks/use-screen-size';
import { AlertTriangle, Bell } from 'lucide-react';

const { useToken } = theme; 

const { Header, Sider, Content, Footer } = Layout;
const { Option } = Select;

export interface I18nConfig {
  languages?: string[];
  defaultLanguage?: string;
}

export interface ShellProps {
  banner?: string;
  bannerAlt?: string;
  bannerLink?: string;
  warning?: ReactNode;
  headerMenuDropdownOpen: boolean;
  setHeaderMenuDropdownOpen: Dispatch<SetStateAction<boolean>>;
  logo: any;
  children: any;
  smallLogo?: any;
  menu: React.ReactElement;
  logout?: React.ReactElement;
  i18nConfig?: I18nConfig;
  collapsed: boolean;
  onCollapse: () => void;
  headerMenu: MenuProps;
  notificationsCount: number;
  onNotificationsVisibilityChange: (visible:boolean) => void;
  notificationsComponent: ReactElement;
  notificationsVisible: boolean;
}

export interface HeaderConfig {
  title: string;
}

export interface UsernameConfig {
  username: string;
}

export interface RoutesConfig {
  routes: { path: string; breadcrumbName: string }[];
}

const headerConfig: State<HeaderConfig> = createState({ title: '(Not set)' });
const routesConfig: State<RoutesConfig> = createState({ routes: [{ path: '', breadcrumbName: '' }] });
const usernameConfig: State<UsernameConfig> = createState({ username: '(Not set)' });

export const setHeader = (title: string) => {
  setTimeout(() => {
    headerConfig.title.set(title);
  }, 10);
};

export const setRoutes = (routes: { path: string; breadcrumbName: string }[]) => {
  setTimeout(() => {
    routesConfig.routes.set(routes);
  }, 10);
};

export const setUsername = (username: string) => {
  setTimeout(() => {
    usernameConfig.username.set(username);
  }, 10);
};

function itemRender(
  route: { path?: string; breadcrumbName?: string },
  params: any,
  routes: { path?: string; breadcrumbName?: string }[],
  paths: string[],
  color: string
) {
  const last = routes.indexOf(route) === routes.length - 1;

  return last ? <span style={{ color }}>{route.breadcrumbName}</span> : <Link to={`/${route.path}`}>{route.breadcrumbName}</Link>;
}

const CustomHeader: FC<{
  onTop: boolean;
  menu: MenuProps;
  username: string;
  dropdownOpen: boolean;
  setDropdownOpen: Dispatch<SetStateAction<boolean>>;
  notificationCount: number;
  notificationComponent: ReactElement;
  onNotificationsVisibilityChange: (visible:boolean)=>void;
  notificationsVisible:boolean;
}> = ({ onTop, username, menu, dropdownOpen, notificationsVisible, setDropdownOpen, notificationComponent, notificationCount, onNotificationsVisibilityChange }) => {
  const { token } = useToken();
  const screenSize = useScreenSize();
  const onMobile = screenSize.width < 600;
  const textVisible = onTop && !onMobile;

  return (
    <div style={{ position: 'absolute', zIndex: 10, right: onMobile ? 0 : 40, padding: 15, overflow: 'hidden' }}>
      <Row>
        <Col style={{position:'relative', left: textVisible ? 0 : 'calc(100% - 158px)', transition:'0.5s', padding: '24px 14px',}}>
          <Badge count={notificationCount} overflowCount={9} offset={[0,-10]} showZero={false}>
            <Popover
              forceRender
              color={token.colorBgLayout}
              rootClassName='notifications-popover'
              open={notificationsVisible}
              content={notificationComponent}
              onOpenChange={(visible)=>onNotificationsVisibilityChange(visible)}
              placement={onMobile ? 'bottom' : 'bottomRight'}
              trigger={['click']}
              arrow
              className="header-menu-dropdown"
            >
              <span
                onClick={(e) => e.preventDefault()}
                style={{
                  cursor: 'pointer',
                  borderColor: token.colorPrimary,
                  borderRadius: 10,
                  borderWidth: 2,
                  borderStyle: 'solid',
                  padding: '20px 10px 10px',
                  backgroundColor: token.colorBgContainer,
                }}
              >
                <Bell size={28} color={token.colorPrimary} fill={token.colorPrimary}/>
              </span>
            </Popover>
          </Badge>
        </Col>
        <Col>
          <Dropdown
            open={dropdownOpen}
            onOpenChange={(v) => setDropdownOpen(v)}
            menu={menu}
            placement="bottomRight"
            trigger={['click']}
            arrow
            className="header-menu-dropdown"
          >
            <span
              onClick={(e) => e.preventDefault()}
              style={{
                cursor: 'pointer',
                borderColor: token.colorPrimary,
                borderRadius: 10,
                borderWidth: 2,
                borderStyle: 'solid',
                padding: '14px 14px 14px',
                backgroundColor: token.colorBgContainer,
                left: textVisible ? 0 : 'calc(100% - 74px)',
                position: 'relative',
                transition: '0.5s',
              }}
            >
              <h5
                style={{
                  display: 'inline-block',
                  opacity: textVisible ? 1 : 0,
                  width: 'fit-content',
                  maxWidth: textVisible ? 'calc(100% - 74px)' : 0,
                  whiteSpace: 'pre',
                  marginRight: textVisible ? 10 : 0,
                  maxHeight: 500,
                  transition: '0.50s',
                }}
              >
                {username}
              </h5>
                  <Avatar shape="square" size={32} icon={<MenuOutlined />} style={{ background: token.colorPrimary, borderRadius: 5 }} />
            </span>
          </Dropdown>
        </Col>
      </Row>
     
      
    </div>
  );
};

const LanguageSelector: FC<{ defaultLanguage?: string; languages?: string[]; onChange?: (key: string) => void }> = ({
  defaultLanguage,
  languages,
  onChange,
}) => {
  const langs = [
    { key: 'en', value: 'English' },
    { key: 'es', value: 'Español' },
    { key: 'fr', value: 'Français' },
    { key: 'ru', value: 'Русский' },
    { key: 'pt', value: 'Português' },
  ];

  const [currentLang, setCurrentLang] = reactUseState<string>(defaultLanguage || 'en');

  const configureLanguage = (key: string) => {
    moment.locale(key);
    i18next.changeLanguage(key);
    setCurrentLang(key);
    HttpService.language = key;
    AuthenticationService.language = key;
  };

  useMemo(() => {
    const savedLang = localStorage.getItem('spt_lang');

    if (savedLang) configureLanguage(savedLang);
    else configureLanguage(defaultLanguage || 'en');
  }, []);

  const onLanguageChanged = (key: string) => {
    localStorage.setItem('spt_lang', key);
    configureLanguage(key);
    onChange && onChange(key);
  };

  const langOptions = langs.filter((l) => (languages || []).includes(l.key || l.value));

  if (langOptions.length <= 1) return null;

  return (
    <div style={{ display: 'inline-block', float: 'right', height: '100%', marginRight: 16 }}>
      <div style={{ display: 'flex', height: '100%', alignItems: 'center' }}>
        <Select value={currentLang} onChange={onLanguageChanged} style={{ width: 120 }}>
          {langOptions.map((l) => (
            <Option key={l.key} value={l.key}>
              {l.value}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

const Shell: FC<ShellProps> = ({
  logo,
  smallLogo,
  menu,
  logout,
  collapsed,
  onCollapse,
  children,
  i18nConfig,
  banner,
  bannerLink,
  headerMenu,
  headerMenuDropdownOpen,
  setHeaderMenuDropdownOpen,
  bannerAlt,
  warning,
  notificationsComponent,
  notificationsCount,
  onNotificationsVisibilityChange,
  notificationsVisible,
  ...props
}) => {
  const headerState = hookstateUseState(headerConfig);
  const routeState = hookstateUseState(routesConfig);
  const usernameState = hookstateUseState(usernameConfig);

  const { token } = useToken();
  const selectedItemColor = token.colorPrimary;
  const [noScroll, setNoScroll] = useState(true);

  return (
    <>
      <CustomHeader
        notificationsVisible={notificationsVisible}
        onNotificationsVisibilityChange={onNotificationsVisibilityChange}
        notificationComponent={notificationsComponent}
        notificationCount={notificationsCount}
        dropdownOpen={headerMenuDropdownOpen}
        setDropdownOpen={setHeaderMenuDropdownOpen}
        menu={headerMenu}
        onTop={noScroll}
        username={usernameState.username.value}
      />

      <Layout>
        {menu && (
          <Sider collapsible collapsed={collapsed} onCollapse={onCollapse} trigger={null}>
            <Layout className="side-layout layout">
              <div className="ant-row ant-row-center ant-row-middle collapsed-trigger" onClick={onCollapse}>
                {collapsed ? <RightOutlined /> : <LeftOutlined />}
              </div>
              <Header style={{ height: '120px' }}>
                <Row justify="center" align="middle">
                  <div className="logo">
                    <Link to="/">{logo && <img src={collapsed && smallLogo ? smallLogo : logo} alt="SPT AB" height={40} />}</Link>
                  </div>
                </Row>
              </Header>
              <Content
                className="site-layout-background"
              >
                {menu}
              </Content>
              <Footer className={collapsed ? 'spt-collapsed-sidebar' : ''}>{logout}</Footer>
            </Layout>
          </Sider>
        )}
        <Layout className="main-layout site-layout" onScroll={(e) => setNoScroll(() => (e.target as HTMLElement).scrollTop < 20)}>
          {banner && bannerLink && (
            <div className="shell-banner">
              <a href={bannerLink} target="_blank" rel="noopener noreferrer">
                <img alt={bannerAlt ?? ''} src={banner}></img>
              </a>
            </div>
          )}
           {banner && !bannerLink && (
            <div className="shell-banner">
              <img alt={bannerAlt ?? ''} src={banner}></img>
            </div>
          )}
          {!banner && bannerAlt && 
            <div className="shell-banner">
               <Alert
                  onClick={bannerLink ? () => window.open(bannerLink, '_blank', 'noopener,noreferrer') : undefined}
                  showIcon 
                  icon={<ExclamationCircleOutlined 
                  style={{fontSize:32, marginRight:16}}/>} 
                  type='info' 
                  style={{margin:10, maxWidth:800, fontWeight:500, fontSize:18}} 
                  message={<div style={{cursor:bannerLink ? 'pointer' : undefined}}>{bannerAlt}</div>}
                />
             </div>}
          <Header className="site-layout-background" style={{ height: 'auto', padding: '40px 40px 0 40px' }}>
            <Row>
              <Col span={24}>
                <div style={{ display: 'inline-block', width: '100%' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <h1 style={{ display: 'inline-block', margin: '0 0 20px 0' }}>{headerState.title.value}</h1>
                      {warning && (  
                        <Alert style={{width:'100%'}} type='warning' showIcon icon={<span style={{color:'#ffa600', marginRight: 16}} role='img'><AlertTriangle size={32}/></span>} message={warning} />
                      )}
                      
                    <Breadcrumb
                      items={routeState.routes.value as { path: string; breadcrumbName: string }[]}
                      itemRender={(
                        route: { path?: string; breadcrumbName?: string },
                        params: any,
                        routes: { path?: string; breadcrumbName?: string }[],
                        paths: string[]
                      ) => itemRender(route, params, routes, paths, selectedItemColor)}
                      separator=">"
                    /> 
                  </div>
                </div>
              </Col>
            </Row>
          </Header>
          <Content
            className="site-layout-background"
            style={{
              margin: '0 16px',
              padding: '0 24px',
              minHeight: 280,
            }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </>
  );
};

export { LanguageSelector };

export default Shell;
